import { ResourceType } from 'types/resources';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import {
  EquityHubTab,
  IVolTab,
  IndicesContentType,
  SentimentTab,
  StrategyCompassMode,
  TraceLense,
} from 'types';
import PeopleIcon from '@mui/icons-material/People';
import { isBloomberg } from '../util/bloomberg'; // need /bloomberg here to avoid recursive deps
import { HiroTab } from './hiro';

const academy = {
  type: ResourceType.EDUCATION,
  name: 'Academy',
  link: 'https://academy.spotgamma.com/?_gl=1*1hrx7ke*_ga*OTcxNjk1NjM5LjE2NjE3ODU0NzE.*_ga_SMJRNHH18H*MTY4NDM2NjMwNy4xMDguMC4xNjg0MzY2MzA3LjAuMC4w',
  description:
    'Trustworthy options education with 6+ hours of premium options education.',
};

const syllabus = {
  type: ResourceType.SUPPORT,
  name: 'Syllabus',
  link: 'https://spotgamma.com/syllabus/',
  description:
    'All the videos and resources from onboarding to maximize your account.',
};

const webinarReplays = {
  type: ResourceType.COMMUNITY,
  name: 'Webinar Replays',
  link: 'https://spotgamma.com/webinar-replays/',
  description:
    'Subscriber-only webinars and exclusive trainings from our team of experts.',
};

const supportCenter = {
  type: ResourceType.SUPPORT,
  name: 'Support Center',
  link: 'https://support.spotgamma.com/hc/en-us',
  description:
    'Supplemental terminology, definitions, and use cases for proprietary terms and trading concepts.',
};

export const MAIN_RESOURCES = [
  academy,
  syllabus,
  webinarReplays,
  supportCenter,
];

export const RESOURCE_LIST = [
  {
    type: ResourceType.EDUCATION,
    resources: [
      {
        name: 'Blog',
        link: 'https://spotgamma.com/blog/',
        description:
          "Gamma news, options market commentary, and timely articles on what's happening in the markets.",
      },
      academy,
      {
        name: 'Free Courses',
        link: 'https://academy.spotgamma.com/course/30-30?_gl=1*1hrx7ke*_ga*OTcxNjk1NjM5LjE2NjE3ODU0NzE.*_ga_SMJRNHH18H*MTY4NDM2NjMwNy4xMDguMC4xNjg0MzY2MzA3LjAuMC4w',
        description:
          'Introductory courses on why options matter with powerful examples of how options drive stocks.',
      },
    ],
  },
  {
    type: ResourceType.COMMUNITY,
    resources: [
      {
        type: ResourceType.COMMUNITY,
        name: 'Discord',
        link: 'resources/discord',
        description:
          'Join the private SpotGamma Discord server, home to a lively and generous community of traders.',
      },
      webinarReplays,
      {
        type: ResourceType.COMMUNITY,
        name: 'Recent Media',
        link: 'https://spotgamma.com/media/#op3-element-6OhpbuG4',
        description:
          'Trusted industry experts, check out the latest articles and media featuring SpotGamma commentary.',
      },
      {
        type: ResourceType.COMMUNITY,
        name: 'Affiliates',
        link: 'https://spotgamma.com/affiliate-dashboard/',
        description:
          'Become a SpotGamma affiliate and get paid to share our analysis with others!',
      },
    ],
  },
  {
    type: ResourceType.SUPPORT,
    resources: [supportCenter, syllabus],
  },
];

export const DISCORD_CONTENT_LIST = [
  {
    icon: <PeopleIcon />,
    name: 'Join the Community',
    description: `Engage with other likeminded traders using SpotGamma's tools to analyze the market. Our community features hundreds of active users ready to share insights, discuss their latest trades, or just chat about the market.`,
  },
  {
    icon: <LightbulbIcon />,
    name: 'Share Trade Ideas',
    description: `Drop your latest trades, or take a peak at what stocks other users are looking at. This private Discord includes SpotGamma content contributors providing their commentary and market insights each week.`,
  },
  {
    icon: <AutoGraphIcon />,
    name: 'Leverage Our Bots',
    description: `Our users can access SpotGamma charts on over 3,500 US stocks to aid themselves and others in their trading research. Discord community members can also tap into third-party tools including charts from TradingView, data look ups, orderbook visualizations, and 24-hour volume.`,
  },
];

export const ZENDESK_ARTICLE_ID_MAPPING: { [key: string]: string } =
  isBloomberg()
    ? {
        'HIRO-watchlist': '20573607040915',
        'HIRO-trending': '18660673979667',
        'hiro-info': '18660578974739',
      }
    : {
        [EquityHubTab.COMPOSITE]: '14356919886227',
        [`${EquityHubTab.COMPOSITE}_synthoi`]: '39946656110739',
        [EquityHubTab.PC_IMPACT]: '8711555931027',
        [`${EquityHubTab.PC_IMPACT}_synthoi`]: '39946646214547',
        [EquityHubTab.LIVE_PRICE]: '14356952534291',
        [`${EquityHubTab.LIVE_PRICE}_synthoi`]: '39946617292691',
        [EquityHubTab.SKEW]: '8711432245779',
        [EquityHubTab.HISTORY]: '14355937614099',
        [EquityHubTab.RR]: '15412858897043',
        [IVolTab.FixedStrikeMatrix]: '23982224020115',
        [IVolTab.TermStructure]: '23982205386131',
        [IVolTab.VolSkew]: '23982082962195',
        [IVolTab.Vix]: '47604412344851',
        'eh-levels-info': '14356859960339',
        scanners: '14356579936403',
        'hiro-info': '4420646443539',
        [IndicesContentType.REAL_TIME_LEVELS]: '15350634159123',
        [IndicesContentType.GAMMA_LEVELS]: '15350709067027',
        [IndicesContentType.TILT]: '15350737304595',
        [IndicesContentType.EXP_CONCENTRATION]: '15350774629523',
        [IndicesContentType.COMBO_STRIKES]: '15350781318291',
        [IndicesContentType.ZERO_DTE]: '15350782964755',
        [IndicesContentType.GAMMA_MODEL]: '15350835468307',
        [IndicesContentType.VANNA_MODEL]: '15350867797267',
        [IndicesContentType.DELTA_MODEL]: '15350839753875',
        [IndicesContentType.VOLFORECAST_MODEL]: '15350841762963',
        [IndicesContentType.OI_VOLUME]: '15350877023123',
        [IndicesContentType.EXP_CONCENTRATION_TABLE]: '15350936322963',
        [IndicesContentType.STRIKE_CONCENTRATION_TABLE]: '15350951180179',
        [IndicesContentType.OPTIONS_RISK_REVERSAL_CHART]: '15350940293523',
        [IndicesContentType.HISTORICAL_CHART]: '15350990363795',
        [IndicesContentType.MAX_REAL_VOL]: '15350875021075', // 2-month / 6-month
        [IndicesContentType.HIST_RETURNS]: '15350947392915', // Realized Volatility
        [IndicesContentType.VOLATILITY]: '15350881328403', // 5/30-day histogram

        'founders-note-info': '15341610402579',
        [SentimentTab.OCC]: '1500006839861',
        [SentimentTab.ZeroDTE]: '15350782964755',
        [SentimentTab.EquityPutCallRatio]: '15417104162835',
        [SentimentTab.ExpirationConcentration]: '15350774629523',
        [StrategyCompassMode.Compass]: '39936624524691',
        [StrategyCompassMode.Freeform]: '39936583267859',

        [HiroTab.StockScreener]: '47874015095827',
        [HiroTab.FlowData]: '36233693393427',
        [HiroTab.ContractData]: '36233745356435',

        'HIRO-trending': '4421139806483',
        'HIRO-watchlist': '4421171646355',
        'HIRO-alerts': '47871269691667',
        'Equity Hub-trending': '4421139806483',
        'Equity Hub-watchlist': '14356000748819',
        [`TRACE-${TraceLense[TraceLense.GAMMA]}`]: '33608037264787',
        [`TRACE-${TraceLense[TraceLense.DELTA_CHARM_DIRECTIONAL]}`]:
          '33608198289043',
        [`TRACE-${TraceLense[TraceLense.DELTA_DIRECTIONAL]}`]: '33608084842643',
        [`TRACE-STRIKE-PLOT`]: '33608227551379',
        'options-calculator': '45971065009299',
      };
